import React, { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import './ToolPage.css'
import './ContrastChecker.css'
import { contrastRatio, wcagLevels } from './utils/contrastChecker'
import { trackEvent } from '../../lib/analytics'
import { useDocumentMeta } from '../../hooks/useDocumentMeta'

const ContrastChecker = () => {
  useDocumentMeta({
    title: 'Verificador de Contraste WCAG | jarder.dev',
    description:
      'Confira se duas cores têm contraste suficiente pelas regras WCAG ' +
      'AA e AAA, com prévia visual ao vivo. Grátis, direto no navegador.',
  })

  const [foreground, setForeground] = useState('#ffffff')
  const [background, setBackground] = useState('#1a1a2e')

  const ratio = useMemo(() => contrastRatio(foreground, background), [
    foreground,
    background,
  ])
  const levels = useMemo(() => (ratio ? wcagLevels(ratio) : null), [ratio])

  const check = () => trackEvent('tool_generate', { tool: 'contrast_checker' })

  return (
    <div className="tool-page">
      <Link to="/ferramentas" className="tool-back-link">
        ← Todas as ferramentas
      </Link>
      <h1>Verificador de Contraste WCAG</h1>
      <p className="tool-disclaimer">
        Baseado na fórmula oficial do WCAG 2.1 de contraste relativo de
        luminância.
      </p>

      <div className="tool-row">
        <div className="tool-field">
          <label htmlFor="fg">Cor do texto</label>
          <div className="contrast-color-row">
            <input
              type="color"
              value={foreground}
              onChange={(e) => setForeground(e.target.value)}
            />
            <input
              id="fg"
              type="text"
              value={foreground}
              onChange={(e) => setForeground(e.target.value)}
              onBlur={check}
            />
          </div>
        </div>
        <div className="tool-field">
          <label htmlFor="bg">Cor de fundo</label>
          <div className="contrast-color-row">
            <input
              type="color"
              value={background}
              onChange={(e) => setBackground(e.target.value)}
            />
            <input
              id="bg"
              type="text"
              value={background}
              onChange={(e) => setBackground(e.target.value)}
              onBlur={check}
            />
          </div>
        </div>
      </div>

      <div
        className="contrast-preview"
        style={{ color: foreground, background }}
      >
        <p style={{ fontSize: '1.6rem', fontWeight: 700 }}>Texto grande de exemplo</p>
        <p>Texto normal de exemplo, pra avaliar a leitura no dia a dia.</p>
      </div>

      {ratio && (
        <div className="tool-result-card">
          <span className="tool-result-label">Razão de contraste</span>
          <span className="tool-result-value">{ratio.toFixed(2)}:1</span>
          <div className="contrast-levels">
            <span className={levels.aaNormal ? 'contrast-pass' : 'contrast-fail'}>
              AA texto normal {levels.aaNormal ? '✓' : '✗'}
            </span>
            <span className={levels.aaLarge ? 'contrast-pass' : 'contrast-fail'}>
              AA texto grande {levels.aaLarge ? '✓' : '✗'}
            </span>
            <span className={levels.aaaNormal ? 'contrast-pass' : 'contrast-fail'}>
              AAA texto normal {levels.aaaNormal ? '✓' : '✗'}
            </span>
            <span className={levels.aaaLarge ? 'contrast-pass' : 'contrast-fail'}>
              AAA texto grande {levels.aaaLarge ? '✓' : '✗'}
            </span>
          </div>
        </div>
      )}

      <div className="tool-info-section">
        <h2>O que significam os níveis AA e AAA</h2>
        <p>
          São os critérios de contraste da WCAG (Web Content
          Accessibility Guidelines), usados para garantir que texto seja
          legível por pessoas com baixa visão. <strong>AA</strong> exige
          contraste mínimo de 4.5:1 para texto normal e 3:1 para texto
          grande (a partir de 18pt ou 14pt em negrito)  é o padrão
          exigido na maioria das diretrizes de acessibilidade web, como a
          Lei Brasileira de Inclusão. <strong>AAA</strong> é mais rígido
          (7:1 e 4.5:1) e recomendado para conteúdo essencial.
        </p>
      </div>
    </div>
  )
}

export default ContrastChecker
